import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import resourcesToBackend from 'i18next-resources-to-backend';

import { LANGUAGE_CODES, LANGUAGE_STORAGE_KEY, DEFAULT_LANGUAGE } from './languages';
import en from './locales/en/apply.json';

// English is bundled so the first paint never waits on a network round trip or
// flashes untranslated keys. Every other locale is a lazily-imported webpack
// chunk, so a visitor downloads exactly one translation file, not six.
i18n
  .use(
    resourcesToBackend(
      (language, namespace) => import(`./locales/${language}/${namespace}.json`)
    )
  )
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    ns: ['apply'],
    defaultNS: 'apply',
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: LANGUAGE_CODES,
    // 'es-MX' / 'pt-PT' / 'fr-CA' all resolve to their base language rather
    // than falling through to English.
    load: 'languageOnly',
    nonExplicitSupportedLngs: true,

    // Bundled English + lazily-loaded everything else requires this flag,
    // otherwise i18next treats the bundled resource as the complete set and
    // never calls the backend.
    resources: { en: { apply: en } },
    partialBundledLanguages: true,

    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      caches: ['localStorage'],
    },

    interpolation: {
      // React already escapes interpolated values.
      escapeValue: false,
    },

    // Suspense is deliberately off. The application form holds a lot of unsaved
    // user input in component state; suspending the subtree mid-form to fetch a
    // language chunk risks throwing that away. Instead t() briefly returns the
    // bundled English string and re-renders when the chunk lands (~one frame on
    // a warm cache), which nobody loses work to.
    react: { useSuspense: false },

    returnEmptyString: false,
  });

// Keep <html lang> in sync with the active language. Without this a screen
// reader keeps reading Spanish or Haitian Creole with an English voice, which
// is close to unintelligible, and search engines mislabel the page.
const syncHtmlLang = (lng) => {
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('lang', lng || DEFAULT_LANGUAGE);
  }
};

syncHtmlLang(i18n.resolvedLanguage || i18n.language);
i18n.on('languageChanged', syncHtmlLang);

export default i18n;
